"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { FileSpreadsheet, FileText } from "lucide-react";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";
import { format } from "date-fns";

interface StaffInfo {
  id: string;
  name: string;
}

interface DailyCompletedDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    dailyData: Array<{
      date: string;
      byStaff: { [staffName: string]: number };
      total: number;
    }>;
    totals: {
      byStaff: { [staffName: string]: number };
      total: number;
    };
  };
}

const COLORS = ["#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#6366f1", "#ec4899", "#14b8a6"];

export function DailyCompletedDashboard({ data }: DailyCompletedDashboardProps) {
  const staffNames = data.staff.map((s) => s.name);

  const getExportData = () => {
    const exportData = data.dailyData.map((row) => {
      const rowData: Record<string, string | number> = {
        Date: format(new Date(row.date), "dd/MM/yyyy"),
      };
      staffNames.forEach((name) => {
        rowData[name] = row.byStaff[name] || 0;
      });
      rowData["Total"] = row.total;
      return rowData;
    });

    // Add totals row
    const totalsRow: Record<string, string | number> = { Date: "TOTAL" };
    staffNames.forEach((name) => {
      totalsRow[name] = data.totals.byStaff[name] || 0;
    });
    totalsRow["Total"] = data.totals.total;
    exportData.push(totalsRow);

    return exportData;
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Daily_Completed_Orders", "Completed Orders");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Daily_Completed_Orders", {
      title: "Daily Completed Orders Report",
      subtitle: "Completed orders breakdown by staff",
      dateRange: data.dateRange,
    });
  };

  // Transform data for chart
  const chartData = data.dailyData.map((row) => ({
    date: row.date,
    ...row.byStaff,
  }));

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {staffNames.slice(0, 3).map((name) => (
          <Card key={name}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {data.totals.byStaff[name] || 0}
              </div>
              <p className="text-xs text-muted-foreground">Completed orders</p>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Grand Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {data.totals.total}
            </div>
            <p className="text-xs text-muted-foreground">All completed orders</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Daily Completed Orders by Staff</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(new Date(value), "dd MMM")}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(value) => format(new Date(value), "dd MMM yyyy")}
              />
              <Legend />
              {staffNames.map((name, index) => (
                <Bar
                  key={name}
                  dataKey={name}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                {staffNames.map((name) => (
                  <TableHead key={name} className="text-right">
                    {name}
                  </TableHead>
                ))}
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.dailyData.map((row) => (
                <TableRow key={row.date}>
                  <TableCell>
                    {format(new Date(row.date), "dd MMM yyyy")}
                  </TableCell>
                  {staffNames.map((name) => (
                    <TableCell key={name} className="text-right">
                      {row.byStaff[name] || 0}
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-medium">
                    {row.total}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/50 font-bold">
                <TableCell>TOTAL</TableCell>
                {staffNames.map((name) => (
                  <TableCell key={name} className="text-right">
                    {data.totals.byStaff[name] || 0}
                  </TableCell>
                ))}
                <TableCell className="text-right">{data.totals.total}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
